import React, { useEffect, useMemo } from "react";
import {faArrowLeft, faChevronRight, faSave} from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import useProjectEditAccess from "@/services/context/project-process/use-project-edit-access";
import usePreventNavigationContext from "@/services/context/prohibit-nagivation/use-prevent-navigation-context.ts";
import useProjectFormNavigationContext from "@/pages/takeoff/context/project-form-navigation/use-project-form-navigation-context.ts";
import useProjectFilePlanContext from "./context/use-project-file-plan-context.ts";
import useModelingToolsContext from "../../../../../../services/context/viewer/modeling-tools/use-modeling-tools-context.ts";
import { useProjectFormHeadingLayoutEffect } from "../../../../context/project-heading/use-project-heading-context.ts";
import Button from "../../../../../../components/buttons/button";
import OverlayPlanViewerLayout from "./overlay-plan-viewer-layout.tsx";
import LoadingButton from "@/components/buttons/loading-button";
import {ProjectHeadingState} from "@/pages/takeoff/context/project-heading/project-heading-context-props.ts";

const ProjectOverlayPlanDetails: React.FC = () => {
    const { canEdit } = useProjectEditAccess();

    const { isDirty, isSaving, building, storey, fileLeft, saveCurrentFileData } = useProjectFilePlanContext();
    const { isInDrawingMode } = useModelingToolsContext();
    const { setRoute } = useProjectFormNavigationContext();

    const { setIsNavigationPrevented, setSaveCallback } = usePreventNavigationContext();

    useEffect(() => {
        setIsNavigationPrevented(isDirty || isSaving || isInDrawingMode);
        return () => setIsNavigationPrevented(false);
    }, [isDirty, isSaving, isInDrawingMode, setIsNavigationPrevented]);

    useEffect(() => {
        setSaveCallback(saveCurrentFileData);
        return () => {
            setSaveCallback(undefined);
        };
    }, [saveCurrentFileData, setSaveCallback]);

    const heading = useMemo<ProjectHeadingState>(
        () => ({
            title:
                <div className='flex flex-row gap-x-1.5 items-center'>
                    <span className='font-semibold'>Superposition</span>
                    <span>:</span>
                    <span>{building?.name}</span>
                    <FontAwesomeIcon icon={faChevronRight} className='text-sm text-gray-600'/>
                    <span>{storey?.name}</span>
                    <FontAwesomeIcon icon={faChevronRight} className='text-sm text-gray-600'/>
                    <span className='text-gray-500'>{fileLeft?.name}</span>
                </div>,
            left:
                <div className='flex flex-row gap-x-2'>
                    <Button variant='simple' className='pointer-events-auto flex flex-row gap-y-2 cursor-pointer' disabled={isSaving || isInDrawingMode} 
                            onClick={() => setRoute('measures')}>
                        <FontAwesomeIcon icon={faArrowLeft} />
                    </Button>

                    {canEdit &&
                        <LoadingButton
                            onClick={saveCurrentFileData}
                            isLoading={isSaving}
                            size="sm"
                            disabled={isInDrawingMode || !isDirty}
                            body={<FontAwesomeIcon icon={faSave} className='text-sm'/>}
                        />
                    }
                </div>
        }), [isSaving, isInDrawingMode, isDirty, building?.name, storey?.name, fileLeft?.name,  canEdit, setRoute, saveCurrentFileData]);

    useProjectFormHeadingLayoutEffect(heading);

    return (
        <OverlayPlanViewerLayout />
    );
}

export default ProjectOverlayPlanDetails;